import React, { useEffect, useRef, useState } from "react";
import CategoryRatingBar from "../CategoryRatingBar";
import PhotoGallery from "../PhotoGallery";
import VerifiedBadge from "../VerifiedBadge";
import Tooltip from "@Components/Tooltip/Tooltip";
import useTranslationHook from "hooks/useTranslationHook";
import Image from "@Components/Image";
import { CategoryRating, PhotoItem } from "../../types";
import { trackAiSummaryInteracted } from "../../analytics";
import { getRandomAvatar } from "../../avatars";
import Verified from "@Icons/Verified";
import styles from "./ratingOverview.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";

interface RatingOverviewProps {
  overallRating: number;
  ratingLabel: string;
  reviewCount: number;
  categories: CategoryRating[];
  summary: string;
  photos: PhotoItem[];
  onMediaClick?: (index: number) => void;
  propertyId?: string | number;
  isHoverPreview?: boolean;
}

const InfoIcon = () => (
  <svg className={styles.infoIcon} width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7.5" stroke="#6B7280" />
    <path d="M8 7V11" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="5" r="0.75" fill="#6B7280" />
  </svg>
);

const STAGGER_DELAY = 0.03; // seconds between each word reveal
const ANIMATION_DURATION = 0.5; // seconds for each word's fade+blur transition
const INITIAL_DELAY = 0.1; // seconds before animation starts

const RatingOverview: React.FC<RatingOverviewProps> = ({
  overallRating,
  ratingLabel,
  reviewCount,
  categories,
  summary,
  photos,
  onMediaClick,
  propertyId,
  isHoverPreview = false,
}) => {
  const { t } = useTranslationHook();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMounted, setHasMounted] = useState(isHoverPreview);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const hasAnimated = useRef(false);

  const summaryWords = summary ? summary.split(" ") : [];

  // Only enable animations after client hydration to preserve SSR visibility
  useEffect(() => {
    if (isHoverPreview) return;
    setHasMounted(true);
  }, [isHoverPreview]);

  useEffect(() => {
    if (!isHoverPreview || !hasMounted || hasAnimated.current) return undefined;

    const timeout = window.setTimeout(() => {
      hasAnimated.current = true;
      setIsVisible(true);
    }, INITIAL_DELAY * 1000);

    return () => clearTimeout(timeout);
  }, [isHoverPreview, hasMounted]);

  useEffect(() => {
    if (isHoverPreview || typeof window === "undefined" || !containerRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isHoverPreview]);

  const scoreHeader = (
    <div className={styles.scoreSection}>
      <div className={styles.overallScoreBlock}>
        <div className={styles.scoreBadge}>
          <span className={styles.scoreNumber}>{overallRating.toFixed(1)}</span>
          <span className={styles.scoreMax}>/5</span>
        </div>
        <div className={styles.scoreInfo}>
          <span className={styles.ratingLabel}>{t(ratingLabel)}</span>
          {reviewCount > 0 && (
            <span className={styles.reviewCount}>
              {reviewCount} {t("propertypage.reviews.title")}
            </span>
          )}
        </div>
      </div>
      <div className={styles.separator} />
      <VerifiedBadge />
    </div>
  );

  return (
    <div
      className={`${styles.container}${isHoverPreview ? ` ${styles.hoverPreview}` : ""}`}
      ref={containerRef}
    >
      {isHoverPreview ? (
        <>
          <div className={styles.hoverHeader}>
            <div className={styles.scoreSection}>
              <div className={styles.overallScoreBlock}>
                <div className={styles.scoreBadge}>
                  <span className={styles.scoreNumber}>{overallRating.toFixed(1)}</span>
                  <span className={styles.scoreMax}>/5</span>
                </div>
                <div className={styles.scoreInfo}>
                  <span className={styles.ratingLabel}>{t(ratingLabel)}</span>
                  <span className={styles.hoverVerifiedRow}>
                    <span className={styles.hoverAvatarStack}>
                      {[
                        { name: "verified-1", index: 0 },
                        { name: "verified-2", index: 1 },
                        { name: "verified-3", index: 2 },
                      ].map(({ name, index }) => (
                        <Image
                          key={name}
                          src={getRandomAvatar(name, index)}
                          alt=""
                          width={20}
                          height={20}
                          className={styles.hoverAvatar}
                        />
                      ))}
                      <span className={styles.hoverCheckmark}>
                        <Verified fill />
                      </span>
                    </span>
                    <span className={styles.reviewCount}>
                      {reviewCount > 0
                        ? `${reviewCount} ${t("propertypage.reviews.verified")} ${t(
                            "propertypage.reviews.studentreview",
                          )}`
                        : `${t("propertypage.reviews.verified")} ${t(
                            "propertypage.reviews.studentreview",
                          )}`}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.categoryGrid}>
            {categories.slice(0, 4).map((cat) => (
              <CategoryRatingBar
                key={cat.label}
                label={cat.label}
                labelKey={cat.labelKey}
                score={cat.score}
                animate={!hasMounted || isVisible}
                compact
              />
            ))}
          </div>
          {summary && (
            <div className={styles.summarySection}>
              <p
                className={`${styles.summaryText} ${
                  !isSummaryExpanded ? styles.hoverSummaryClamp : ""
                }`}
              >
                {summaryWords.map((word, idx) => (
                  <span
                    key={idx}
                    className={
                      hasMounted
                        ? `${styles.revealWord} ${isVisible ? styles.revealWordVisible : ""}`
                        : undefined
                    }
                    style={
                      hasMounted
                        ? {
                            transitionDelay: isVisible
                              ? `${INITIAL_DELAY + idx * STAGGER_DELAY}s`
                              : "0s",
                            transitionDuration: `${ANIMATION_DURATION}s`,
                          }
                        : undefined
                    }
                  >
                    {word}
                    {idx < summaryWords.length - 1 ? "\u00A0" : ""}
                  </span>
                ))}
              </p>
              {!isSummaryExpanded && (
                <span
                  role="button"
                  tabIndex={0}
                  className={styles.readMoreLink}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsSummaryExpanded(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsSummaryExpanded(true);
                    }
                  }}
                >
                  {t("searchpage.searchheadingcontent.readmore") || "Read More"}
                </span>
              )}
            </div>
          )}
        </>
      ) : (
        <div className={styles.topRow}>
          {scoreHeader}
          <div className={styles.categoryGrid}>
            {categories.map((cat) => (
              <CategoryRatingBar
                key={cat.label}
                label={cat.label}
                labelKey={cat.labelKey}
                score={cat.score}
                animate={!hasMounted || isVisible}
              />
            ))}
          </div>
        </div>
      )}

      {!isHoverPreview && summary && (
        <div className={styles.summarySection}>
          <p className={styles.summaryText}>
            {summaryWords.map((word, idx) => (
              <span
                key={idx}
                className={
                  hasMounted
                    ? `${styles.revealWord} ${isVisible ? styles.revealWordVisible : ""}`
                    : undefined
                }
                style={
                  hasMounted
                    ? {
                        transitionDelay: isVisible
                          ? `${INITIAL_DELAY + idx * STAGGER_DELAY}s`
                          : "0s",
                        transitionDuration: `${ANIMATION_DURATION}s`,
                      }
                    : undefined
                }
              >
                {word}
                {idx < summaryWords.length - 1 ? "\u00A0" : ""}
              </span>
            ))}
          </p>
          {!isHoverPreview && (
            <div
              className={`${styles.summarySource} ${
                !hasMounted || isVisible ? styles.summarySourceVisible : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (propertyId) {
                  trackAiSummaryInteracted({ propertyId, actionType: "CLICK" });
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && propertyId) {
                  trackAiSummaryInteracted({ propertyId, actionType: "CLICK" });
                }
              }}
              style={
                hasMounted
                  ? {
                      transitionDelay: isVisible
                        ? `${INITIAL_DELAY + summaryWords.length * STAGGER_DELAY + 0.3}s`
                        : "0s",
                    }
                  : undefined
              }
            >
              <span className={styles.summarySourceText}>
                {t("propertypage.reviews.summarysource")}
              </span>
              <Tooltip
                content={t("propertypage.reviews.aisummarytooltip")}
                direction="top"
                className={styles.tooltip}
                classNameWrapper={styles.tooltipWrapper}
                addComponent
                delay={0}
              >
                <InfoIcon />
              </Tooltip>
            </div>
          )}
        </div>
      )}

      {!isHoverPreview && <PhotoGallery photos={photos} onMediaClick={onMediaClick} />}
    </div>
  );
};

export default wrapperHOC(RatingOverview, {
  componentName: "RatingOverview",
  showForChina: true,
});
